import { useEffect, useReducer, useRef, useState } from 'react';
import { TRANSACTION_SWR_OPTIONS } from '@/hooks/queries/swrConfig';

// A space write changes what transaction rows say they were sent to (`Transaction.spaceExpenses`, VER-571). The SWR
// caches heal through revalidateTransactionCaches; a mounted infinite list, whose aggregate a filter mutate never
// reaches, and the rows a screen holds outside SWR (an open invoice's charges, the detail drawer's fallback row)
// listen here and read their rows again.
type Listener = () => void;

let listeners: Listener[] = [];

export function notifySpaceSendingChanged(): void {
  for (const listener of listeners) listener();
}

export function useOnSpaceSendingChanged(listener: Listener): void {
  const latest = useRef(listener);
  useEffect(() => {
    latest.current = listener;
  });
  useEffect(() => {
    const handle = () => latest.current();
    listeners = [...listeners, handle];
    return () => {
      listeners = listeners.filter((other) => other !== handle);
    };
  }, []);
}

type Reread = (landed: () => boolean) => void;

// A screen's rows read again after a space write. A reread applies its answer only when `landed()` says so: while it is
// the newest reread and nothing else wrote those rows since it started. A newer reread supersedes it; a write it never
// saw sends it back, through `again`, to read the rows shown now. The screen's own reads (its first page, "Carregar
// mais") take a ticket from `reading` when they are requested and settle it when their rows are applied: one requested
// before a reread started may carry rows from before the space write, so it asks for another reread; so does a rename
// (`useTransactionRename`'s `reading`). Any other write to the rows (a row opened from another list, a local patch)
// calls `wrote`.
export function createReread(again: () => void): {
  start: (reread: Reread) => void;
  wrote: () => void;
  reading: () => () => void;
} {
  let generation = 0;
  let writes = 0;
  return {
    start: (reread) => {
      const mine = ++generation;
      const writesAtStart = writes;
      reread(() => {
        if (mine !== generation) return false;
        if (writes === writesAtStart) return true;
        again();
        return false;
      });
    },
    wrote: () => {
      writes += 1;
    },
    reading: () => {
      const generationAtStart = generation;
      return () => {
        writes += 1;
        if (generation !== generationAtStart) again();
      };
    },
  };
}

// A reread starts once React has committed what was asked before it, so it reads the rows on screen, not the ones a
// render still to come replaces. The rows are also read again when the person comes back to the tab
// (`useOnReturnToTab`).
export function useRereadOnSpaceWrite(reread: Reread): { wrote: () => void; reading: () => () => void } {
  const latest = useRef(reread);
  useEffect(() => {
    latest.current = reread;
  });
  const [asked, ask] = useReducer(countUp, 0);
  const [guard] = useState(() => createReread(ask));
  useEffect(() => {
    if (asked > 0) guard.start(latest.current);
  }, [guard, asked]);
  useOnSpaceSendingChanged(ask);
  useOnReturnToTab(ask);
  return { wrote: guard.wrote, reading: guard.reading };
}

// The person came back to the tab, at most once per the transaction lists' focus throttle: a space write made in
// another tab sends no signal here, and the rows' markers are read again on the way back, as the SWR lists are.
export function useOnReturnToTab(listener: Listener): void {
  const latest = useRef(listener);
  useEffect(() => {
    latest.current = listener;
  });
  useEffect(() => {
    let last = Date.now();
    const handle = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - last < TRANSACTION_SWR_OPTIONS.focusThrottleInterval) return;
      last = now;
      latest.current();
    };
    window.addEventListener('focus', handle);
    document.addEventListener('visibilitychange', handle);
    return () => {
      window.removeEventListener('focus', handle);
      document.removeEventListener('visibilitychange', handle);
    };
  }, []);
}

function countUp(count: number): number {
  return count + 1;
}
